import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  forwardRef,
} from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import findIndex from "lodash/findIndex";
import Table from "react-bootstrap/Table";

import { withDroppable } from "../../hoc/withDroppableContext";
import { DnDHeader, Header, Footer } from "..";
import DraggableRow from "../DraggableRow";
import { multiSelectTo as multiSelect } from "./utils";

const DraggableTable = forwardRef((props, ref) => {
  const {
    columns,
    bordered,
    classes,
    onClickRow,
    onDoubleClickRow,
    selectOnlyOneRow,
    unselectAll,
    isInvalidRow,
    getSelectedRows,
    droppableProps,
    tableProps,
    children,
    isDraggableTable,
    draggingRowId,
    draggableKeyId,
    onColumnOrderChanged,
    getRowProps,
    setIsPopoverHidden,
    getSortBy,
    selectable,
    stretchColumnWidth,
    tableRef,
    useHookPagination,
  } = props;

  const [resizer, setResizer] = useState(false);
  const [click, setClick] = useState(0);
  const [event, setEvent] = useState();
  const [row, setRow] = useState();
  const [prevRow, setPrevRow] = useState();

  const prevRowRef = useRef();

  useEffect(() => {
    prevRowRef.current = row;
  }, [row]);

  const sortableColumns = useMemo(
    () =>
      columns.reduce((acc, column) => {
        column.defaultCanSort && acc.push(column.accessor);

        return acc;
      }, []),
    [columns]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    visibleColumns,
    setColumnOrder,
    toggleAllRowsSelected,
    toggleAllPageRowsSelected,
    selectedFlatRows,
    state,
    manualSortBy,
  } = tableProps;

  // use 'page' instead of 'rows', which has only the rows for the active page
  const memoRows = useMemo(() => (useHookPagination ? page : rows), [
    useHookPagination,
    page,
    rows,
  ]);

  const selectedRowsIds = useMemo(
    () => (selectedFlatRows || []).map(({ id }) => id),
    [selectedFlatRows]
  );

  useEffect(() => unselectAll && toggleAllRowsSelected(false), [
    unselectAll,
    toggleAllRowsSelected,
  ]);

  useEffect(
    () => getSelectedRows?.(selectedFlatRows.map(row => row?.original)),
    [selectedFlatRows]
  );

  useEffect(() => {
    if (manualSortBy && getSortBy) {
      getSortBy(state?.sortBy);
    }
  }, [JSON.stringify(state?.sortBy), manualSortBy]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (click === 1) onClickRow(event, row);
      setClick(0);
    }, 250);
    // the duration between this click and the previous one
    // is less than the value of delay = double-click
    if (click === 2 && prevRow?.id === row?.id) onDoubleClickRow(event, row);

    return () => clearTimeout(timer);
  }, [click]);

  const commitSelection = useCallback(
    items => {
      items.forEach(item => {
        memoRows.find(({ id }) => id === item)?.toggleRowSelected(true);
      });
    },
    [memoRows]
  );
  /**
   * Toggle selection in group
   */
  const toggleSelectionInGroup = useCallback(
    taskId => {
      const index = selectedRowsIds.indexOf(taskId);

      // if not selected - add it to the selected items
      if (index === -1) {
        return commitSelection([...selectedRowsIds, taskId]);
      }

      // it was previously selected and now needs to be removed from the group
      const shallow = [...selectedRowsIds];
      shallow.splice(index, 1);

      commitSelection(shallow);
    },
    [selectedRowsIds, commitSelection]
  );

  /**
   * Multi select to
   * This behaviour matches the MacOSX finder selection
   */
  const multiSelectTo = useCallback(
    newTaskId => {
      const updated = multiSelect(memoRows, selectedRowsIds, newTaskId);

      if (updated == null) {
        return;
      }

      commitSelection(updated);
    },
    [memoRows, selectedRowsIds, commitSelection]
  );

  /**
   * Toggle selection
   */
  const toggleSelection = useCallback(
    taskId => {
      const wasSelected = selectedRowsIds.includes(taskId);

      const newTaskIds = (() => {
        // Task was not previously selected
        // now will be the only selected item
        if (!wasSelected) {
          return [taskId];
        }

        // Task was part of a selected group
        // will now become the only selected item
        if (selectedRowsIds.length > 1) {
          return [taskId];
        }

        // task was previously selected but not in a group
        // we will now clear the selection
        return [];
      })();

      commitSelection(newTaskIds);
    },
    [selectedRowsIds, commitSelection]
  );

  /**
   * Was toggle in selection group key used
   * Determines if the platform specific toggle selection in group key was used
   */
  const wasToggleInSelectionGroupKeyUsed = e => {
    const isUsingWindows = navigator.platform.indexOf("Win") >= 0;
    return isUsingWindows ? e.ctrlKey : e.metaKey;
  };

  /**
   * Was multi select key used
   * Determines if the multiSelect key was used
   */
  const wasMultiSelectKeyUsed = e => e.shiftKey;

  /**
   * Perform action
   */
  const performAction = useCallback(
    (e, record) => {
      if (!toggleAllRowsSelected || !toggleAllPageRowsSelected) return;

      if (!selectOnlyOneRow && wasMultiSelectKeyUsed(e)) {
        if (useHookPagination) {
          toggleAllPageRowsSelected(false);
          toggleSelectionInGroup(record.id);
          return;
        }

        toggleAllRowsSelected(false);
        multiSelectTo(record.id);
        return;
      }

      if (!selectOnlyOneRow && wasToggleInSelectionGroupKeyUsed(e)) {
        useHookPagination
          ? toggleAllPageRowsSelected(false)
          : toggleAllRowsSelected(false);
        toggleSelectionInGroup(record.id);
        return;
      }

      // Handle click on row when checkbox column is available
      if (selectable) {
        return record.toggleRowSelected(!record.isSelected);
      } else {
        useHookPagination
          ? toggleAllPageRowsSelected(false)
          : toggleAllRowsSelected(false);
        toggleSelection(record.id);
      }
    },
    [
      toggleAllRowsSelected,
      toggleAllPageRowsSelected,
      selectOnlyOneRow,
      selectable,
      toggleSelectionInGroup,
      multiSelectTo,
      toggleSelection,
      useHookPagination,
    ]
  );

  const clickHandler = useCallback(
    (e, row) => {
      const prevRowValue = prevRowRef.current;
      // persist() is needed because of event pooling https://reactjs.org/docs/legacy-event-pooling.html
      e.persist();

      e.currentTarget?.focus();

      !event && setEvent(e);
      setRow(row);
      setPrevRow(prevRowValue);
      setClick(click => click + 1);

      performAction(e, row);
    },
    [prevRowRef, event, performAction]
  );

  const handleColumnOrderChanged = useCallback(
    (...props) => {
      setColumnOrder(...props);
      onColumnOrderChanged && onColumnOrderChanged(...props);
    },
    [setColumnOrder]
  );

  // Disable text selection while pressing shift
  const onMouseDown = useCallback(e => {
    if (wasMultiSelectKeyUsed(e)) {
      e.preventDefault();
    }
  }, []);

  const onKeyDown = useCallback(
    (e, row) => {
      if (e.code === "Enter") {
        e.preventDefault();
        e.stopPropagation();

        clickHandler(e, row);
      }
    },
    [clickHandler]
  );

  return (
    <>
      <div
        ref={node => {
          if (ref) {
            if (typeof ref === "function") {
              ref(node);
            } else {
              ref.current = node;
            }
          }
          if (tableRef) {
            tableRef.current = node;
          }
        }}
        className={classNames("mydpd-dndtable-container", classes.container)}
      >
        <Table
          bordered={bordered}
          hover
          responsive="sm"
          className={classNames("mydpd-dndtable", classes.table)}
          {...getTableProps()}
        >
          {!resizer && setColumnOrder ? (
            <DnDHeader
              headerGroups={headerGroups}
              sortableColumns={sortableColumns}
              visibleColumns={visibleColumns}
              setColumnOrder={handleColumnOrderChanged}
              setResizer={setResizer}
              resizer={resizer}
              setIsPopoverHidden={setIsPopoverHidden}
              selectable={selectable}
              stretchColumnWidth={stretchColumnWidth}
            />
          ) : (
            <Header
              headerGroups={headerGroups}
              sortableColumns={sortableColumns}
              setResizer={setResizer}
              resizer={resizer}
              stretchColumnWidth={stretchColumnWidth}
            />
          )}
          <tbody {...getTableBodyProps()} {...droppableProps}>
            {memoRows.map((row, index) => {
              prepareRow(row);
              const selectedRowProps =
                typeof row.getToggleRowSelectedProps === "function"
                  ? row.getToggleRowSelectedProps()
                  : {};
              const draggableId = draggableKeyId
                ? row.original[draggableKeyId]
                : row.id;
              const isGhosting =
                selectedRowProps.checked &&
                Boolean(draggingRowId) &&
                draggingRowId !== draggableId;

              // Set selectionCount only when dragging item from selection
              const selectionCount =
                (findIndex(selectedFlatRows, ({ id }) => row.id === id) !==
                  -1 &&
                  selectedFlatRows?.length) ||
                0;

              return (
                <DraggableRow
                  row={row}
                  key={draggableId}
                  draggableId={draggableId}
                  selectionCount={selectionCount}
                  isDraggableTable={isDraggableTable}
                  onClick={e => clickHandler(e, row)}
                  onMouseDown={onMouseDown}
                  onKeyDown={e => onKeyDown(e, row)}
                  index={index}
                  selectedRowProps={selectedRowProps}
                  className={classNames(
                    selectedRowProps.checked && "mydpd-dndtable-row-selected",
                    isInvalidRow?.(row.original) && classes.invalidRow,
                    isGhosting && classes.ghostingRow
                  )}
                  getRowProps={getRowProps}
                  stretchColumnWidth={stretchColumnWidth}
                />
              );
            })}
            {droppableProps &&
              droppableProps.placeholder &&
              droppableProps.placeholder}
          </tbody>
        </Table>
      </div>
      {children && <Footer>{children}</Footer>}
    </>
  );
});

DraggableTable.defaultProps = {
  classes: {},
  columns: [],
  data: [],
  tableHooks: [],
  onClickRow: () => null,
  onDoubleClickRow: () => null,
  initialState: {},
  unhiddenColumns: [],
  onChangeColumnsSelection: () => null,
  isVisiblePopover: false,
  selectOnlyOneRow: false,
  unselectAll: false,
  getRowProps: () => ({}),
  stretchColumnWidth: true,
  useHookPagination: false,
};

DraggableTable.propTypes = {
  droppableProps: PropTypes.shape({
    placeholder: PropTypes.node,
  }),
  columns: PropTypes.array,
  classes: PropTypes.object,
  row: PropTypes.object,
  onClickRow: PropTypes.func,
  onDoubleClickRow: PropTypes.func,
  isInvalidRow: PropTypes.func,
  getSelectedRows: PropTypes.func,
  children: PropTypes.node,
  bordered: PropTypes.bool,
  selectOnlyOneRow: PropTypes.bool,
  unselectAll: PropTypes.bool,
  isDraggableTable: PropTypes.bool,
  draggingRowId: PropTypes.string,
  draggableKeyId: PropTypes.string,
  tableProps: PropTypes.shape({
    getTableProps: PropTypes.func,
    getTableBodyProps: PropTypes.func,
    headerGroups: PropTypes.array,
    rows: PropTypes.array,
    page: PropTypes.array,
    prepareRow: PropTypes.func,
    visibleColumns: PropTypes.array,
    setColumnOrder: PropTypes.func,
    toggleAllRowsSelected: PropTypes.func,
    toggleAllPageRowsSelected: PropTypes.func,
    selectedFlatRows: PropTypes.array,
    state: PropTypes.object,
    manualSortBy: PropTypes.bool,
  }),
  onColumnOrderChanged: PropTypes.func,
  getRowProps: PropTypes.func,
  setIsPopoverHidden: PropTypes.func,
  getSortBy: PropTypes.func,
  selectable: PropTypes.bool,
  stretchColumnWidth: PropTypes.bool,
  useHookPagination: PropTypes.bool,
  tableRef: PropTypes.object,
};

export default withDroppable(DraggableTable);
